import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, MapPin, Factory, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const IndustrialEPCSomerset = () => {
  const towns = ['Taunton', 'Bridgwater', 'Yeovil', 'Frome', 'Glastonbury', 'Shepton Mallet', 'Chard', 'Wellington', 'Minehead', 'Street'];
  const industrialEstates = [
    { name: 'Blackbrook Business Park', location: 'Taunton', desc: 'Major employment site near junction 25 of the M5' },
    { name: 'Chelston Business Park', location: 'Wellington', desc: 'Growing industrial area serving mid-Somerset' },
    { name: 'Huntworth Business Park', location: 'Bridgwater', desc: 'Modern industrial and logistics park near M5 junction 24' },
    { name: 'Lynx Trading Estate', location: 'Yeovil', desc: 'Established industrial estate in South Somerset' },
    { name: 'Marston Trading Estate', location: 'Frome', desc: 'Industrial hub serving East Somerset' }
  ];

  const localSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Industrial EPC Assessments Somerset",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Performance Direct",
      "areaServed": { "@type": "State", "name": "Somerset" }
    },
    "areaServed": { "@type": "State", "name": "Somerset" },
    "serviceType": "Industrial Energy Performance Certificate"
  };

  return (
    <>
      <Helmet>
        <title>Industrial EPC Somerset | Warehouse & Factory EPCs Taunton, Bridgwater</title>
        <meta name="description" content="Industrial EPC assessments across Somerset including Taunton, Bridgwater, Yeovil and Frome. Professional warehouse and factory EPC surveys for Somerset industrial properties." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/industrial-epc-somerset" />
        <script type="application/ld+json">{JSON.stringify(localSchema)}</script>
      </Helmet>
      
      <Navbar />
      <Breadcrumbs items={[{ label: 'Industrial EPC', path: '/industrial-epc' }, { label: 'Somerset', path: '/industrial-epc-somerset' }]} />
      
      <section className="bg-gradient-to-br from-amber-700 via-amber-600 to-amber-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-200 px-4 py-2 rounded-full text-sm mb-6">
              <MapPin className="w-4 h-4" /> Somerset
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Industrial EPC Assessments in Somerset</h1>
            <p className="text-xl text-amber-200 mb-8">Professional warehouse, factory and workshop EPC surveys across Taunton, Bridgwater, Yeovil and throughout Somerset.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-amber-700 hover:bg-gray-100">
                <Link to="/booking?service=commercial_warehouse">Get Somerset EPC Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077"><Phone className="mr-2 w-5 h-5" />Call 01392 980077</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Industrial EPC Services Across Somerset</h2>
            <p className="text-gray-700">
              Somerset's industrial landscape spans aerospace engineering in Yeovil, food processing across the Levels, and logistics operations along the M5 corridor. Energy Performance Direct provides professional <Link to="/industrial-epc" className="text-amber-600 hover:underline">industrial EPC assessments</Link> throughout the county for landlords, developers and business owners requiring compliant energy certificates.
            </p>
            <p className="text-gray-700">
              Our accredited non-domestic energy assessors cover all major Somerset towns including Taunton, Bridgwater, Yeovil, Frome, Glastonbury, Shepton Mallet, Chard, Wellington, Minehead and Street. We regularly assess industrial properties on business parks near M5 junctions and throughout the county's established trading estates.
            </p>
            <p className="text-gray-700">
              Somerset benefits from excellent motorway access, making it a prime location for distribution centres and logistics facilities. Many industrial buildings in the county are modern constructions with good energy performance, while older agricultural and manufacturing buildings may require improvement to meet MEES regulations.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Somerset Industrial Areas We Cover</h2>
            <div className="not-prose grid md:grid-cols-2 gap-4">
              {industrialEstates.map((estate, i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900">{estate.name}</h3>
                  <p className="text-sm text-amber-600 mb-2">{estate.location}</p>
                  <p className="text-sm text-gray-600">{estate.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Towns Covered in Somerset</h2>
            <p className="text-gray-700">We provide industrial EPC assessments throughout Somerset including:</p>
            <div className="not-prose flex flex-wrap gap-2 mt-4">
              {towns.map((town, i) => (
                <span key={i} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">{town}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Factory className="w-6 h-6 text-amber-600 mb-2" />
                <h3 className="font-semibold">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">All industrial building types</p>
              </Link>
              <Link to="/warehouse-epc" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Building2 className="w-6 h-6 text-amber-600 mb-2" />
                <h3 className="font-semibold">Warehouse EPCs</h3>
                <p className="text-sm text-gray-600">Distribution and storage</p>
              </Link>
              <Link to="/commercial-epc-somerset" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Building2 className="w-6 h-6 text-amber-600 mb-2" />
                <h3 className="font-semibold">Commercial EPCs Somerset</h3>
                <p className="text-sm text-gray-600">All commercial buildings</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Your Somerset Industrial EPC Quote</h2>
          <p className="text-xl text-green-100 mb-8">Professional assessments throughout Somerset.</p>
          <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
            <Link to="/booking?service=commercial_warehouse">Request Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default IndustrialEPCSomerset;